import { prop, reject, equals } from 'ramda';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSnackbar } from '#hooks';
import services from '#services';
import { actions } from '#store';

const useNewUser = () => {
  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
  const state = useSelector(prop('newuser'));
  const dispatch = useDispatch();

  const create = useCallback(async ({ values, enableSnackbar, successSnackbar } = {}) => {
    try {
      dispatch(actions.newuser.create.creator());
      const body = {
        ...reject(equals(''))(values),
      };
      const newuser = await services.bob.newuser.create(body);
      dispatch(actions.newuser.createSuccess.creator({ payload: newuser }));
      if (enableSnackbar && successSnackbar) {
        enqueueSuccessSnackbar(successSnackbar);
      }
      return newuser;
    } catch (err) {
      dispatch(actions.newuser.createError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => create({ values, enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch, enqueueSuccessSnackbar]);

  // TODO: Not Used yet, delete this comment when it comes
  const fetch = useCallback(async (query, { enableSnackbar } = {}) => {
    try {
      dispatch(actions.newuser.fetch.creator());
      const newuser = await services.bob.newuser.getAll(query);
      dispatch(actions.newuser.fetchSuccess.creator({ payload: newuser }));
      return newuser;
    } catch (err) {
      dispatch(actions.newuser.fetchError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => fetch(query, { enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch]);

  const update = useCallback(async (id, { values, enableSnackbar, successSnackbar } = {}) => {
    try {
      dispatch(actions.newuser.update.creator());
      // Removed role & company from allowed updated fields
      const { role, company, ...body } = reject(equals(''))(values);
      const newuser = await services.bob.newuser.update(id, body);
      dispatch(actions.newuser.updateSuccess.creator({ payload: newuser }));
      if (enableSnackbar && successSnackbar) {
        enqueueSuccessSnackbar(successSnackbar);
      }
      return newuser;
    } catch (err) {
      dispatch(actions.newuser.updateError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => update(id, { values, enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch, enqueueSuccessSnackbar]);

  const remove = useCallback(async (id, { values, enableSnackbar, successSnackbar } = {}) => {
    try {
      dispatch(actions.newuser.remove.creator());
      const newuser = await services.bob.newuser.remove(id);
      dispatch(actions.newuser.removeSuccess.creator({ payload: newuser }));
      if (enableSnackbar && successSnackbar) {
        enqueueSuccessSnackbar(successSnackbar);
      }
      return newuser;
    } catch (err) {
      dispatch(actions.newuser.removeError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => remove(id, { values, enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch, enqueueSuccessSnackbar]);

  return {
    ...state,
    handlers: {
      create,
      fetch,
      remove,
      update,
    },
  };
};

export default useNewUser;
